/**
 * The web transport.
 *
 * The phone carries a bearer token in the Keychain. A browser must not: anything JavaScript can
 * read, a cross-site script can exfiltrate, and a session in `localStorage` survives the tab,
 * the day and the shared laptop. So the web session is an **HttpOnly, Secure, SameSite=Lax
 * cookie** the browser attaches by itself and this code never sees.
 *
 * That choice costs one thing and this file pays it: a cookie the browser attaches
 * automatically is attached on a cross-site request too, which is what CSRF is. `SameSite=Lax`
 * blocks the cross-site POST, and the double-submit token below is the belt to that brace —
 * the server sets a readable `__Host-rb_csrf` cookie, and every state-changing request echoes
 * it in a header. An attacker on another origin can cause the cookie to be sent but cannot read it, so
 * cannot produce the header.
 *
 * Everything is parsed with zod from `@refbooth/core`, the same schemas the phone parses. A
 * malformed report fails here, at the boundary, and not three components deep inside a chart.
 */

import type { z } from 'zod';
import { Upload } from 'tus-js-client';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** Present when the route is gated and the account's plan does not reach it. */
    readonly requiredPlan?: string,
  ) {
    super(message);
  }
}

/**
 * Same origin, always.
 *
 * Caddy proxies `/api/*` on each host to the API, so the browser makes a first-party request,
 * there is no CORS anywhere, and the session cookie is not a third-party cookie in any browser's
 * eyes — which matters more every year.
 */
const BASE = '/api';

/**
 * The CSRF token the server set, under whichever name this host uses.
 *
 * **Two names, and missing the first one breaks every write.** In production the cookie carries
 * the `__Host-` prefix — host-only, no `Domain`, `Secure` required — which is what keeps an
 * admin session on panel.refbooth.com from ever being writable by another subdomain. A browser
 * refuses to store a `__Host-` cookie over plain http, so on localhost the server drops the
 * prefix and sets `rb_csrf` instead.
 *
 * A reader that knows only the bare name finds nothing in production, sends no header, and
 * every POST comes back 403 — while working perfectly on a laptop. Both names are read, longest
 * first, so the prefixed one wins where both somehow exist.
 */
function csrfToken(): string | null {
  for (const name of ['__Host-rb_csrf', 'rb_csrf']) {
    const match = document.cookie.match(
      new RegExp(`(?:^|;\\s*)${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]+)`),
    );
    if (match) return decodeURIComponent(match[1]);
  }
  return null;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** A FormData body is sent as-is; the browser sets its own multipart boundary. */
  form?: FormData;
  /**
   * Extra headers, merged in before the CSRF token and content type are set — so a caller adds
   * to the request (a share link's `X-Share-Password`) but cannot use this to override either.
   */
  headers?: Record<string, string>;
}

export async function request<T>(
  path: string,
  schema: z.ZodType<T>,
  options: RequestOptions = {},
): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json', ...options.headers };

  if (method !== 'GET') {
    // **Both headers, or the server refuses.** `X-RefBooth-Request` is the primary CSRF defence
    // for a JSON API: any request carrying a custom header is preflighted by every browser, and
    // a page on another origin cannot get a preflight approved against an API that grants no
    // CORS. The double-submit token below is the backstop. The server checks for both, so a
    // client that sends only one gets a 403 that looks like a bug in the session.
    headers['X-RefBooth-Request'] = '1';
    const token = csrfToken();
    if (token) headers['X-CSRF-Token'] = token;
  }

  let body: BodyInit | undefined;
  if (options.form) {
    body = options.form;
  } else if (options.body !== undefined) {
    body = JSON.stringify(options.body);
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body,
    signal: options.signal,
    // The session cookie is first-party and same-origin; `same-origin` is both correct and
    // narrower than `include`, which would also send it to a redirect off this host.
    credentials: 'same-origin',
  });

  if (!response.ok) {
    let detail = `The request failed (${response.status}).`;
    let requiredPlan: string | undefined;
    try {
      const payload = (await response.json()) as { detail?: unknown; required_plan?: unknown };
      if (typeof payload.detail === 'string') detail = payload.detail;
      if (typeof payload.required_plan === 'string') requiredPlan = payload.required_plan;
    } catch {
      /* A proxy error page is not JSON. The status is still the useful part. */
    }
    throw new ApiError(detail, response.status, requiredPlan);
  }

  if (response.status === 204) return schema.parse(undefined);
  return schema.parse(await response.json());
}

/**
 * A resumable upload, with progress — tus 1.0.0 through tus-js-client (MIT).
 *
 * A 400 MB WAV over a hotel line or a phone drops at 92 % and a single POST starts again from
 * zero; a producer on a slow link never gets a file through (the operator, 2026-09-15). tus
 * sends the file in chunks, and after any drop asks the server how far it got (HEAD →
 * Upload-Offset) and continues from there — retried on a backoff, no page reload needed. The
 * server side is `POST/HEAD/PATCH /v1/uploads` in services/api; when the last byte lands it
 * admits the track and names it in `X-RefBooth-Track-Id`, and this helper fetches the row.
 *
 * Nothing is written to the browser's storage: resume lives in memory for the life of the page
 * (`storeFingerprintForResuming: false`), which is the case that matters — a flaky line, not a
 * closed tab.
 */
export function uploadResumable(
  file: File,
  metadata: Record<string, string>,
  onProgress?: (fraction: number) => void,
): { promise: Promise<{ trackId: string }>; abort: () => void } {
  let instance: Upload | null = null;
  const promise = new Promise<{ trackId: string }>((resolve, reject) => {
    const headers: Record<string, string> = { 'X-RefBooth-Request': '1' };
    const token = csrfToken();
    if (token) headers['X-CSRF-Token'] = token;
    instance = new Upload(file, {
      // Absolute on purpose: the server's Location is relative (`uploads/{id}`), and
      // tus-js-client resolves it against the endpoint — a relative endpoint would resolve
      // against the page instead and PATCH `/uploads/{id}` on the SPA (a 405, seen live).
      endpoint: `${window.location.origin}${BASE}/v1/uploads`,
      chunkSize: 8 * 1024 * 1024,
      retryDelays: [0, 1000, 3000, 5000, 10000, 20000],
      storeFingerprintForResuming: false,
      removeFingerprintOnSuccess: true,
      headers,
      metadata,
      onProgress: (sent, total) => {
        if (onProgress && total > 0) onProgress(sent / total);
      },
      onError: (error) => {
        const status = (error as { originalResponse?: { getStatus: () => number } }).originalResponse?.getStatus?.() ?? 0;
        let detail = status ? `The upload failed (${status}).` : 'The connection dropped before the file finished uploading.';
        const body = (error as { originalResponse?: { getBody: () => string } }).originalResponse?.getBody?.();
        if (body) {
          try {
            const payload = JSON.parse(body) as { detail?: unknown };
            if (typeof payload.detail === 'string') detail = payload.detail;
          } catch {
            /* not JSON */
          }
        }
        reject(new ApiError(detail, status));
      },
      onAfterResponse: (_req, res) => {
        const id = res.getHeader('X-RefBooth-Track-Id');
        if (id) resolve({ trackId: id });
      },
      onSuccess: () => {
        // The last PATCH carried the track id through onAfterResponse; if a proxy stripped the
        // header, ask the upload itself.
        const url = instance?.url;
        if (!url) { reject(new ApiError('The upload finished but the track could not be found.', 0)); return; }
        fetch(url, { credentials: 'same-origin' })
          .then((r) => r.json())
          .then((status: { track?: { id?: string } }) => {
            if (status.track?.id) resolve({ trackId: status.track.id });
            else reject(new ApiError('The upload finished but the track could not be found.', 0));
          })
          .catch(() => reject(new ApiError('The upload finished but the track could not be found.', 0)));
      },
    });
    instance.start();
  });
  return {
    promise,
    abort: () => {
      // `abort(true)` also asks the server to drop the staged bytes (DELETE); a cancelled
      // upload is an ApiError with status 0, as before.
      void instance?.abort(true);
    },
  };
}

/**
 * A single-request upload, with progress — kept for the multipart routes that are not files
 * a producer waits minutes for.
 *
 * `fetch` still cannot report request progress in any shipping browser, and a producer sending a
 * 400 MB WAV over a hotel connection needs to see that something is happening — a spinner with
 * no movement for four minutes reads as a hang, and they cancel. So uploads use XHR, which has
 * reported upload progress since 2008, and everything else uses fetch.
 */
export function upload<T>(
  path: string,
  form: FormData,
  schema: z.ZodType<T>,
  onProgress?: (fraction: number) => void,
): { promise: Promise<T>; abort: () => void } {
  const xhr = new XMLHttpRequest();
  const promise = new Promise<T>((resolve, reject) => {
    xhr.open('POST', `${BASE}${path}`);
    xhr.withCredentials = true;
    // The same two headers `request()` sends — an upload is a state-changing request like any
    // other, and the server's CSRF check does not make an exception for large ones.
    xhr.setRequestHeader('X-RefBooth-Request', '1');
    const token = csrfToken();
    if (token) xhr.setRequestHeader('X-CSRF-Token', token);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) onProgress(event.loaded / event.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(schema.parse(JSON.parse(xhr.responseText)));
        } catch (error) {
          reject(error);
        }
        return;
      }
      let detail = `The upload failed (${xhr.status}).`;
      try {
        const payload = JSON.parse(xhr.responseText) as { detail?: unknown };
        if (typeof payload.detail === 'string') detail = payload.detail;
      } catch {
        /* not JSON */
      }
      reject(new ApiError(detail, xhr.status));
    };
    xhr.onerror = () =>
      reject(new ApiError('The connection dropped before the file finished uploading.', 0));
    xhr.onabort = () => reject(new ApiError('Upload cancelled.', 0));
    xhr.send(form);
  });

  return { promise, abort: () => xhr.abort() };
}
